/**
 * TableOfContents Component
 *
 * Displays article headings with smooth scroll navigation
 */

import { useState, useEffect } from "react";
import { List } from "lucide-react";
import type { TableOfContentsItem } from "../../content/blog/types";

interface TableOfContentsProps {
  items: TableOfContentsItem[];
  className?: string;
}

export function TableOfContents({
  items,
  className = "",
}: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: "-80px 0px -80% 0px",
        threshold: 0,
      },
    );

    // Observe all heading elements
    items.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [items]);

  const handleClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80; // Account for sticky header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  if (items.length === 0) return null;

  return (
    <nav className={`bg-gray-50 rounded-lg p-4 ${className}`}>
      <div className="flex items-center gap-2 mb-3 text-gray-700">
        <List className="h-4 w-4" />
        <span className="font-medium text-sm">Table of Contents</span>
      </div>
      <ul className="space-y-1">
        {items.map((item) => {
          const isActive = activeId === item.id;
          const indent =
            item.level === 1 ? "" : item.level === 2 ? "pl-3" : "pl-6";

          return (
            <li key={item.id}>
              <button
                onClick={() => handleClick(item.id)}
                className={`w-full text-left text-sm py-1.5 px-2 rounded transition-colors ${indent} ${
                  isActive
                    ? "text-teal-600 bg-teal-50 font-medium"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                }`}
              >
                {item.title}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export default TableOfContents;
